import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, PhoneCall } from "lucide-react";
import { calcEstimate, fmt } from "@/data/pricing";
import { createLead } from "@/lib/api";

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4 py-2.5 border-b border-line-dark text-sm"><span className="text-linen/50">{k}</span><span className="text-linen text-right">{v}</span></div>
);

export const EstimatorResult = ({ s, onReset }) => {
  const est = calcEstimate(s);
  const [form, setForm] = useState({ full_name: "", phone: "", email: "", suburb_postcode: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const lead = await createLead({ ...form, estimate: { product: s.product, style: s.style, hardware: s.hardware, tier: s.tier, function: s.fn, windows: s.windows, size: s.size, addons: s.addons, low: est.low, high: est.high } });
      setDone(lead);
      toast.success("Estimate locked in — we'll call you within one business day.");
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-16">
      <div>
        <p className="eyebrow mb-4">Your estimated range</p>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="h-display text-4xl sm:text-5xl lg:text-6xl text-linen" data-testid="estimate-range">
          {fmt(est.low)} <span className="text-brass italic">–</span> {fmt(est.high)}
        </motion.p>
        <p className="mt-3 text-sm text-linen/50">≈ {fmt(est.perWindow)} per window · supply{s.addons.includes("Professional Installation") ? " + install" : " only"} · incl. GST</p>
        <div className="mt-8" data-testid="estimate-summary">
          <Row k="Product" v={s.product} /><Row k="Style" v={s.style} /><Row k="Hardware" v={s.hardware} />
          <Row k="Tier" v={s.tier} /><Row k="Function" v={s.fn} /><Row k="Windows" v={`${s.windows} × ${s.size}`} />
          <Row k="Add-ons" v={s.addons.length ? s.addons.join(", ") : "None"} />
        </div>
        <button type="button" onClick={onReset} className="mt-6 text-xs font-mono uppercase tracking-[0.2em] text-linen/50 hover:text-brass transition-colors" data-testid="estimator-reset-btn">Start over</button>
      </div>

      <div className="bg-ink-3 border border-line-dark p-7 lg:p-9">
        {done ? (
          <div className="h-full flex flex-col justify-center text-center py-8" data-testid="lead-success">
            <div className="mx-auto h-14 w-14 rounded-full bg-brass text-ink flex items-center justify-center"><PhoneCall size={22} /></div>
            <h3 className="font-serif text-3xl text-linen mt-6">Estimate locked in, {done.full_name.split(" ")[0]}.</h3>
            <p className="mt-3 text-sm text-linen/60 leading-relaxed">Reference <span className="font-mono text-brass">{done.id.slice(0, 8).toUpperCase()}</span>. A confirmation is on its way to {done.email}. Our Melbourne team will call to arrange your free in-home measure.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="lead-form">
            <div className="flex items-center gap-2 text-linen"><Lock size={16} className="text-brass" /><h3 className="font-serif text-2xl">Lock in this estimate</h3></div>
            <p className="text-xs text-linen/50">We'll hold this price range for 30 days and book your free measure.</p>
            <div><label className="label" htmlFor="lead-name">Full name</label><input id="lead-name" required minLength={2} className="field-dark" placeholder="Alex Nguyen" value={form.full_name} onChange={upd("full_name")} data-testid="lead-name-input" /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="label" htmlFor="lead-phone">Phone</label><input id="lead-phone" required minLength={6} type="tel" className="field-dark" placeholder="04xx xxx xxx" value={form.phone} onChange={upd("phone")} data-testid="lead-phone-input" /></div>
              <div><label className="label" htmlFor="lead-email">Email</label><input id="lead-email" required type="email" className="field-dark" placeholder="you@email.com" value={form.email} onChange={upd("email")} data-testid="lead-email-input" /></div>
            </div>
            <div><label className="label" htmlFor="lead-suburb">Suburb / Postcode</label><input id="lead-suburb" required minLength={3} className="field-dark" placeholder="Hawthorn 3122" value={form.suburb_postcode} onChange={upd("suburb_postcode")} data-testid="lead-suburb-input" /></div>
            <button type="submit" disabled={busy} className="btn-brass w-full h-14 disabled:opacity-60" data-testid="lead-submit-btn">{busy ? "Locking in…" : "Lock in estimate & request booking"}</button>
            <p className="text-[11px] text-linen/40 text-center">No spam. No obligation. Final price confirmed at your free measure.</p>
          </form>
        )}
      </div>
    </div>
  );
};
